import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpService } from '../http.service';
import {
  Faq,
  FaqCategory,
  FaqCategoryListResponse,
  FaqListResponse,
  PublicFaqListResponse,
} from '../../../models/admin/faqs.model';
import { SubmitResponse } from '../../../models/common/api-response.model';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FaqsService extends HttpService<any, FaqListResponse> {
  constructor(httpClient: HttpClient, @Inject('API_URL') apiUrl: string, @Inject('API_VERSION') apiVersion: string) {
    super(httpClient, apiUrl, apiVersion, 'faqs');
  }

  getFaqsList(
    page: number = 1,
    limit: number = 10,
    filters: {
      search?: string | null;
      isActive?: boolean | null;
      faqCategoryId?: string | number | null;
    } = {}
  ): Observable<FaqListResponse> {
    const hasSearch = !!filters.search;
    const hasStatus = filters.isActive !== null && filters.isActive !== undefined;
    const hasCategory =
      filters.faqCategoryId !== null &&
      filters.faqCategoryId !== undefined &&
      filters.faqCategoryId !== '';

    const params: Record<string, string | number | boolean> = {};

    if (hasSearch) {
      params['search'] = filters.search as string;
    }
    if (hasStatus) {
      params['is_active'] = filters.isActive as boolean;
    }
    if (hasCategory) {
      params['faq_category_id'] = filters.faqCategoryId as string | number;
    }
    if (!hasSearch && !hasStatus && !hasCategory) {
      params['page'] = page;
      params['limit'] = limit;
    }

    return this.getBy('', params) as unknown as Observable<FaqListResponse>;
  }

  getFaqById(id: string | number): Observable<Faq> {
    return this.getById(id) as unknown as Observable<Faq>;
  }

  addNewFaq(faq: any): Observable<Faq> {
    return this.create(faq) as unknown as Observable<Faq>;
  }

  updateFaq(id: string | number, faq: any): Observable<SubmitResponse> {
    return this.patch(id, faq) as unknown as Observable<SubmitResponse>;
  }

  deleteFaq(id: string | number): Observable<SubmitResponse> {
    return this.delete(id) as unknown as Observable<SubmitResponse>;
  }

  updateFaqStatus(id: string | number): Observable<SubmitResponse> {
    return this.patch(id, undefined, '', 'status') as unknown as Observable<SubmitResponse>;
  }

  // #region FAQ Categories
  getFaqsCategoriesList(
    page: number = 1,
    limit: number = 10,
    filters: { search?: string | null; isActive?: boolean | null } = {}
  ): Observable<FaqCategoryListResponse> {
    const hasSearch = !!filters.search;
    const hasStatus = filters.isActive !== null && filters.isActive !== undefined;

    const params: Record<string, string | number | boolean> = {};

    if (hasSearch) {
      params['search'] = filters.search as string;
    }
    if (hasStatus) {
      params['is_active'] = filters.isActive as boolean;
    }
    if (!hasSearch && !hasStatus) {
      params['page'] = page;
      params['limit'] = limit;
    }

    return this.getBy('categories', params) as unknown as Observable<FaqCategoryListResponse>;
  }

  getCategoryById(id: string | number): Observable<FaqCategory> {
    return this.getById(id, 'categories') as unknown as Observable<FaqCategory>;
  }

  addNewCategory(category: any): Observable<FaqCategory> {
    return this.create(category, 'categories') as unknown as Observable<FaqCategory>;
  }

  updateCategory(id: string | number, category: any): Observable<SubmitResponse> {
    return this.patch(id, category, 'categories') as unknown as Observable<SubmitResponse>;
  }

  deleteCategory(id: string | number): Observable<SubmitResponse> {
    return this.delete(id, 'categories') as unknown as Observable<SubmitResponse>;
  }

  updateCategoryStatus(id: string | number): Observable<SubmitResponse> {
    return this.patch(id, undefined, 'categories', 'status') as unknown as Observable<SubmitResponse>;
  }

  getPublicFAQs(
    query?: string,
    category_id?: string | number
  ): Observable<PublicFaqListResponse> {
    const params: Record<string, string | number> = {};
    if (query) params['search'] = query;
    if (category_id !== undefined && category_id !== null && category_id !== '') {
      params['faq_category_id'] = category_id;
    }
    return this.getBy('public', params) as unknown as Observable<PublicFaqListResponse>;
  }
  
  getPublicFAQsCategories(): Observable<FaqCategory[]> {
    return this.getBy('public/categories') as unknown as Observable<FaqCategory[]>;
  }

}
